// Display helpers only. The point tables used to live here too, mirrored from
// the Go scorer, and the two drifted: the card knew six signals while the
// scorer had nine, so the table could not add up to the score in its own
// header. The backend now returns its breakdown and the card renders it.
export function formatPts(pts: number) {
  if (pts === 0) return "—"
  return `+${pts}`
}

// Lower-cases a signal label for use inside a sentence, but leaves acronyms
// alone: "Concurrent sessions" reads better lower-cased, "IP addresses" and
// "OS families" do not.
function midSentence(label: string) {
  if (/^[A-Z]{2}/.test(label)) return label
  return label.charAt(0).toLowerCase() + label.slice(1)
}

export type RiskSignal = {
  Key: string
  Label: string
  Count: number
  Points: number
  Values: string[]
  Note?: string | null
}

// One line answering "why is this user at this level", so a reader does not
// have to scan nine rows for the ones that are not a dash. Ordered by
// contribution, since that is the order an operator cares about.
export function summarizeDrivers(signals: RiskSignal[]) {
  const drivers = signals
    .filter((signal) => signal.Points > 0)
    .sort((a, b) => b.Points - a.Points)
  if (drivers.length === 0) return null

  const names = drivers.map(
    (signal) => `${midSentence(signal.Label)} (+${signal.Points})`,
  )
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
  return `Driven by ${list}.`
}

// Signals whose values identify individual sign-ins, so clicking one can filter
// the list below the card. Devices and OS families are deliberately excluded:
// their values are collapsed identity keys (see the Go normalize.go), so they
// do not match a raw sign-in row without reimplementing that collapsing here.
const DRILLABLE_SIGNALS = new Set([
  "ips",
  "locations",
  "countries",
  "locationSpread",
])

export function isDrillable(signalKey: string) {
  return DRILLABLE_SIGNALS.has(signalKey)
}

// A clicked signal value, kept with the signal it came from so filtering can
// match against the field the value was counted from.
export type FocusedSignal = {
  key: string
  value: string
}

type MatchableActivity = {
  IpAddress?: string | null
  Location?: string | null
  Country?: string | null
}

const IPV6_GROUP_SUFFIX = "::/48"

// Whether a sign-in row is behind a clicked signal value. Matching is per
// field, not a blanket substring across every column: country "SE" must not
// match a city containing "se", and IP "10.0.0.1" must not match "110.0.0.12".
// Country and location values are exact strings from the rows themselves. An
// IPv6 value is a /48 group ("2a05:9cc4:40b::/48"), so it prefix-matches the
// full addresses behind it; any other IP value is the raw address.
export function activityMatchesFocused(
  activity: MatchableActivity,
  focused: FocusedSignal,
) {
  switch (focused.key) {
    case "countries":
      return activity.Country === focused.value
    case "locations":
    case "locationSpread":
      return activity.Location === focused.value
    case "ips": {
      const ip = activity.IpAddress?.toLowerCase()
      if (!ip) return false
      if (!focused.value.endsWith(IPV6_GROUP_SUFFIX)) {
        return ip === focused.value.toLowerCase()
      }
      const base = focused.value
        .slice(0, -IPV6_GROUP_SUFFIX.length)
        .toLowerCase()
      // Match on a group boundary so "…:40b" does not catch "…:40bf". A base
      // shorter than three groups means the /48's third group is zero, which a
      // canonical address writes as a compressed run ("…::…") or a literal 0.
      if (base.split(":").length >= 3) {
        return ip.startsWith(`${base}:`)
      }
      return ip.startsWith(`${base}::`) || ip.startsWith(`${base}:0:`)
    }
    default:
      return false
  }
}

// The scored period, and whether thresholds were widened for it. Accumulating
// signals (IPs, locations, devices, countries, session overlaps) can only grow
// as the period widens, so their thresholds scale with it.
export function formatWindow(days: number, scale: number) {
  const period = days === 1 ? "24 hours" : `${days} days`
  if (scale <= 1) {
    return `Scored over ${period} against the calibrated thresholds.`
  }
  return `Scored over ${period}; accumulating thresholds widened ${scale.toFixed(1)}x for the period.`
}
